import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { Research, ResearchTag } from '../../../../types/Research'
import {
  fetchResearchById,
  updateResearch,
  addResearchTag,
  fetchResearchTags,
} from '../../../../utils/researchUtils'
import useCanManageResearch from '../../../../hooks/useCanManageResearch'

export interface EditResearchFormData {
  title: string
  publicationYear: string
  journal: string
  url: string
  doi: string
  abstract: string
  researcherNotes: string
}

export const useEditResearch = (id: string | undefined) => {
  const navigate = useNavigate()
  const toast = useToast()
  const canManageResearch = useCanManageResearch()

  // State management
  const [research, setResearch] = useState<Research | null>(null)
  const [tags, setTags] = useState<ResearchTag[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [newTagName, setNewTagName] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showTagError, setShowTagError] = useState(false)

  // Form setup
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EditResearchFormData>({
    defaultValues: {
      title: '',
      publicationYear: '',
      journal: '',
      url: '',
      doi: '',
      abstract: '',
      researcherNotes: '',
    },
  })

  // Data loading effect
  useEffect(() => {
    const loadData = async () => {
      if (!id) {
        navigate('/research')
        return
      }

      try {
        setLoading(true)
        const [researchData, tagsData] = await Promise.all([
          fetchResearchById(id),
          fetchResearchTags(),
        ])

        if (!researchData) {
          toast({
            title: 'Research not found',
            description: 'The requested research article could not be found',
            status: 'error',
            duration: 5000,
            isClosable: true,
          })
          navigate('/research')
          return
        }

        setResearch(researchData)
        setTags(tagsData)
        setSelectedTags(researchData.tags)

        // Populate form with existing data
        setValue('title', researchData.title)
        setValue('publicationYear', researchData.publicationYear)
        setValue('journal', researchData.journal)
        setValue('url', researchData.url)
        setValue('doi', researchData.doi || '')
        setValue('abstract', researchData.abstract)
        setValue('researcherNotes', researchData.researcherNotes || '')
      } catch (error) {
        console.error('Error loading data:', error)
        toast({
          title: 'Error loading research',
          description: 'There was an error loading the research data',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [id, navigate, toast, setValue])

  // Tag management functions
  const handleAddNewTag = async () => {
    if (!newTagName.trim()) return

    try {
      setSaving(true)
      const newTagId = await addResearchTag({
        name: newTagName.trim(),
        createdAt: new Date() as any,
        createdBy: 'current-user', // This should be replaced with actual user ID
      })

      const newTag: ResearchTag = {
        id: newTagId,
        name: newTagName.trim(),
        createdAt: new Date() as any,
        createdBy: 'current-user',
      }

      setTags((prev) => [...prev, newTag])
      setSelectedTags((prev) => [...prev, newTagId])
      setNewTagName('')
      setShowTagError(false)
    } catch (error) {
      console.error('Error creating tag:', error)
      toast({
        title: 'Error creating tag',
        description: 'There was an error creating the new tag',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveTag = (tagId: string) => {
    setSelectedTags((prev) => prev.filter((id) => id !== tagId))
    setShowTagError(false)
  }

  const handleSelectTag = (tagId: string) => {
    if (!selectedTags.includes(tagId)) {
      setSelectedTags((prev) => [...prev, tagId])
      setShowTagError(false)
    }
  }

  // Form submission
  const onSubmit = async (data: EditResearchFormData) => {
    if (selectedTags.length === 0) {
      setShowTagError(true)
      return
    }

    if (!research?.id) return

    try {
      setSaving(true)

      const updatedData = {
        ...research,
        ...data,
        tags: selectedTags,
      }

      await updateResearch(research.id, updatedData)

      toast({
        title: 'Research updated',
        description: 'The research article has been successfully updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      navigate(`/research/${research.id}`)
    } catch (error) {
      console.error('Error updating research:', error)
      toast({
        title: 'Error updating research',
        description: 'There was an error updating the research article',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setSaving(false)
    }
  }

  return {
    // Data
    research,
    tags,
    selectedTags,
    newTagName,

    // State
    loading,
    saving,
    showTagError,
    canManageResearch,

    // Form
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    isSubmitting,

    // Tag management
    setNewTagName,
    handleAddNewTag,
    handleRemoveTag,
    handleSelectTag,
    setShowTagError,

    // Navigation
    onCancel: () => navigate(`/research/${id}`),
  }
}
